/**
 * Der Ablauf einer Übungsrunde — für Hören UND Geben derselbe. (06.10.2026, Morse-Überarbeitung)
 *
 * Eine Aufgabe: dafür sorgen, dass „Stopp“ wirklich stoppt, nichts nachläuft und nichts verloren geht.
 *   - `token`: jede Fortsetzung (Ton zu Ende, Uhr abgelaufen) gilt nur, solange er steht — `allesAus()` macht
 *     alle ungültig, hält die EINE Uhr an und den Ton (morse-ton.ts, `stopp`);
 *   - der Ton bleibt während der Runde wach (`halteWach`/`lassWach`, morse-ton.ts `wachHalten`);
 *   - die aktive Zeit (Pausen zählen nicht) für die Buchung;
 *   - die Buchungen (Prüfbefunde W4, K6, M11): jede mit einer KENNUNG (Runde + laufende Nummer). Was nicht
 *     ankommt, bleibt mit derselben Kennung in der Warteschlange — über neue Runden hinweg —, und die Zeile
 *     „Nicht gespeichert … Nochmal speichern / Verwerfen“ steht da, bis es gebucht oder verworfen ist. Der Server
 *     bucht eine Kennung nur einmal (memory/morse-store.ts): „Nochmal speichern“ nach verlorener Antwort und das
 *     Nachbuchen beim Verlassen der Seite können nicht doppelt zählen.
 */
import type { Umfang } from "../../shared/morse-koch.js";
import { baueMeldung, el, knopf, meldeIn, type Buehne, type BuchungsAntwort, type MorseBuchungsArt, type MorseUmgebung } from "./morse-teile.js";

/** Eine Buchung, wie sie an den Server geht — und bei einem Fehler unverändert noch einmal. */
export interface Satz {
  kennung: string;
  art: MorseBuchungsArt;
  aufgaben: Array<{ soll: string; ist: string }>;
  dauerMs: number;
  /** Nur bei „Alle Zeichen“ gesetzt: die Buchung zählt dann nicht für den Aufstieg (der Server entscheidet es daran). */
  umfang?: Umfang;
}

/** Eine Kennung für die Runde: Zeit und Zufall, nur Buchstaben und Ziffern (MORSE_KENNUNG im Speicher). */
function rundenKennung(art: string, zufall: () => number = Math.random): string {
  const z = Math.floor(zufall() * 36 ** 6)
    .toString(36)
    .padStart(6, "0");
  return `${art}-${Date.now().toString(36)}-${z}`;
}

export class Ablauf {
  /** Jede Fortsetzung gilt nur, solange dieser Zähler steht. */
  token = 0;
  /** Die aktive Zeit der Runde (ms) bis zur letzten Unterbrechung — Pausen zählen nicht. */
  aktivMs = 0;
  /** Ein Aufstieg, den eine Buchung dieser Runde gebracht hat (für die Zusammenfassung). */
  aufstieg: BuchungsAntwort | null = null;
  private runde = "";
  private umfang: Umfang = "meine";
  private nummer = 0;
  private gebuchtMs = 0;
  private uhr: ReturnType<typeof setTimeout> | null = null;
  private seit: number | null = null;
  private wach: { los: () => void } | null = null;
  /** Nicht angekommen — wartet auf „Nochmal speichern“ (oder die nächste Buchung). */
  private offen: Satz[] = [];
  /** Gerade unterwegs (Kennungen) — ein zweiter Versuch derselben Buchung läuft nicht daneben. */
  private unterwegs = new Set<string>();
  /** Die laufenden Anfragen — `warte()` wartet auf alle. */
  private laufend = new Set<Promise<boolean>>();
  private letzterFehler = "";
  private readonly zeile: HTMLElement;
  private readonly zeileText: HTMLElement;

  constructor(
    private readonly u: MorseUmgebung,
    private readonly art: MorseBuchungsArt,
    /** Die Bühne: die Zeile „Nicht gespeichert …“ steht unter ihrer Statuszeile, über der Fläche. */
    b: Buehne,
  ) {
    this.zeile = el("div", "morse-speicher");
    this.zeile.hidden = true;
    this.zeileText = baueMeldung();
    const knoepfe = el("div", "morse-aktionen");
    knoepfe.append(
      knopf("Nochmal speichern", "morse-knopf morse-knopf--klein", () => void this.nochmal()),
      knopf("Verwerfen", "morse-knopf morse-knopf--klein", () => this.verwerfen()),
    );
    this.zeile.append(this.zeileText, knoepfe);
    b.wurzel.insertBefore(this.zeile, b.flaeche);
  }

  /**
   * Eine neue Runde beginnt: Zeit auf null, neue Kennung. Offene Buchungen alter Runden bleiben in der Schlange.
   * `umfang`: womit die Runde geübt wird — jede Buchung der Runde trägt es (bei „alle“) bis in die Schlange.
   */
  neueRunde(umfang: Umfang = "meine"): void {
    this.umfang = umfang;
    this.runde = rundenKennung(this.art === "hoeren" ? "h" : this.art === "sprechen" ? "s" : "g", () => this.u.zufall());
    this.nummer = 0;
    this.aktivMs = 0;
    this.gebuchtMs = 0;
    this.aufstieg = null;
  }

  /* ── Uhr, Ton, Zeit ─────────────────────────────────────────────── */

  /** Sofort still: keine Fortsetzung gilt mehr, keine Uhr, kein Ton. */
  allesAus(): void {
    this.token++;
    this.uhrAus();
    try {
      this.u.spieler.stopp();
    } catch {
      /* egal */
    }
  }

  /** `f` nach `ms` — nur, wenn bis dahin nichts die Runde angehalten hat. Es gibt EINE Uhr. */
  spaeter(ms: number, f: () => void): void {
    this.uhrAus();
    const tok = this.token;
    this.uhr = setTimeout(() => {
      this.uhr = null;
      if (tok === this.token) f();
    }, Math.max(0, ms));
  }

  uhrAus(): void {
    if (this.uhr) clearTimeout(this.uhr);
    this.uhr = null;
  }

  /**
   * Den Ton JETZT wecken (aus der Geste) und für die Runde wach halten. Die Diagnosezeile `spurZeile` kommt mit
   * dem Zustand des Tons, sobald klar ist, ob er wach ist; `ohneTon` läuft, wenn nicht.
   */
  halteWach(spurZeile: string, ohneTon?: () => void): void {
    // Eine Übung beginnt oder geht weiter: die Hörspur schweigt (sie hat den Spieler sonst gleich in der Hand).
    this.u.hoerspur?.stille("uebung");
    this.lassWach();
    const h = this.u.spieler.wachHalten();
    this.wach = h;
    void h.wach.then((ok) => {
      this.u.spur(`${spurZeile} ton=${this.u.spieler.zustand()}${ok ? "" : " wach=nein"}`);
      if (!ok) ohneTon?.();
    });
  }

  lassWach(): void {
    this.wach?.los();
    this.wach = null;
  }

  zeitLaeuft(): void {
    this.seit = this.u.jetzt();
  }

  zeitHalt(): void {
    this.aktivMs = this.aktivJetzt();
    this.seit = null;
  }

  /** Die aktive Zeit bis JETZT (eine laufende Strecke zählt mit). */
  private aktivJetzt(): number {
    return this.aktivMs + (this.seit !== null ? Math.max(0, this.u.jetzt() - this.seit) : 0);
  }

  /* ── Buchen ─────────────────────────────────────────────────────── */

  /** Eine Buchung bilden: neue Kennung, Dauer seit der letzten Buchung dieser Runde. */
  satz(aufgaben: Array<{ soll: string; ist: string }>): Satz {
    const jetzt = this.aktivJetzt();
    const dauerMs = Math.max(0, Math.round(jetzt - this.gebuchtMs));
    this.gebuchtMs = jetzt;
    return { kennung: `${this.runde}-${this.nummer++}`, art: this.art, aufgaben, dauerMs, ...(this.umfang === "alle" ? { umfang: this.umfang } : {}) };
  }

  /**
   * Senden. `keepalive`: die Seite geht gerade (verdeckt, `pagehide`) — die Anfrage soll den Abgang überleben.
   * Wahr, wenn gebucht. Scheitert es, bleibt der Satz in der Schlange und die Zeile sagt es in Worten.
   */
  sende(s: Satz, keepalive = false): Promise<boolean> {
    if (this.unterwegs.has(s.kennung)) return Promise.resolve(false);
    this.unterwegs.add(s.kennung);
    const p = this.sendeEinmal(s, keepalive).finally(() => {
      this.unterwegs.delete(s.kennung);
      this.laufend.delete(p);
    });
    this.laufend.add(p);
    return p;
  }

  /** Bis alle laufenden Buchungen eine Antwort haben (gelungen oder nicht). */
  async warte(): Promise<void> {
    while (this.laufend.size) await Promise.allSettled([...this.laufend]);
  }

  private async sendeEinmal(s: Satz, keepalive: boolean): Promise<boolean> {
    const r = await this.u.buche(s.art, s.aufgaben, s.dauerMs, { kennung: s.kennung, keepalive, umfang: s.umfang });
    if (!r.ok) {
      if (!this.offen.some((x) => x.kennung === s.kennung)) this.offen.push(s);
      this.letzterFehler = r.fehler;
      this.u.spur(`buchung fehler reiter=${s.art} aufgaben=${s.aufgaben.length} offen=${this.offen.length}`);
      this.zeigeOffen();
      return false;
    }
    this.offen = this.offen.filter((x) => x.kennung !== s.kennung);
    if (r.daten.aufgestiegen) this.aufstieg = r.daten;
    this.zeigeOffen();
    // Ging vorher etwas nicht, versucht es die nächste gelungene Buchung gleich mit.
    if (this.offen.length && !keepalive) void this.nochmal();
    return true;
  }

  /** Alles Offene noch einmal senden — dieselben Kennungen. */
  async nochmal(): Promise<void> {
    for (const s of [...this.offen]) await this.sende(s);
  }

  /** Was nicht gespeichert ist, bewusst wegwerfen. */
  verwerfen(): void {
    this.u.spur(`buchung verworfen reiter=${this.art} offen=${this.offen.length}`);
    this.offen = [];
    this.zeigeOffen();
  }

  /** Steht noch etwas aus? */
  get hatOffenes(): boolean {
    return this.offen.length > 0;
  }

  private zeigeOffen(): void {
    const n = this.offen.length;
    this.zeile.hidden = n === 0;
    if (!n) return;
    meldeIn(this.zeileText, `Nicht gespeichert${n > 1 ? ` (${n} Teile)` : ""}: ${this.letzterFehler || "unbekannter Fehler."}`, "fehler");
  }
}
